import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import { useEquipmentLabels } from '@/hooks/useEquipmentLabels';
import { usePrintPage } from '@/hooks/usePrintPage';
import LabelSheet from '@/components/labels/LabelSheet';
import { ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { MAX_COPIES, STICKERS_PER_PAGE, copiesFrom, paginate, repeat } from '@/lib/labelSheet';

// The sheet is drawn to the millimetre, so the paper is A4 with no margin of
// the browser's own.
const PRINT_PAGE = 'size: A4 portrait; margin: 0;';

// Everything that is not a sheet is left out of the print.
const PRINT_CSS = `
@media print {
  html, body { background: #fff !important; margin: 0 !important; }
  .label-controls { display: none !important; }
  .label-sheets { padding: 0 !important; gap: 0 !important; background: #fff !important; }
  .label-sheet { box-shadow: none !important; break-after: page; break-inside: avoid; }
  .label-sheet:last-child { break-after: auto; }
}
`;

/**
 * Stickers for equipment, ready to print, with the DIGGER logo, the code the
 * app scans, and the name. An equipment has one code however many units it
 * has, so its stickers are copies of one another, as many as are asked for.
 * The page sits outside the backoffice's frame, so that what is printed is
 * the sheets and nothing else.
 */
export default function EquipmentLabelsPage() {
  usePrintPage(PRINT_PAGE);

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = Number(searchParams.get('centro'));
  const centreId = Number.isInteger(requested) && requested > 0 ? requested : null;

  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];
  const labelsQuery = useEquipmentLabels(centreId);
  const labels = labelsQuery.data?.labels ?? [];

  // Every label is printed unless it is unticked. Keeping what is left out,
  // not what is kept, means equipment added later is included by default.
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  // How many of each, as typed. What is not here is one.
  const [typed, setTyped] = useState<Readonly<Record<number, string>>>({});
  const chosen = labels.filter((l) => !excluded.has(l.id));
  const stickers = repeat(chosen, (l) => copiesFrom(typed[l.id]));
  const pages = paginate(stickers);
  const hasUnits = labels.some((l) => (l.quantity ?? 1) > 1);

  const toggle = (id: number) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setCopies = (id: number, value: string) => setTyped((prev) => ({ ...prev, [id]: value }));

  /** What was typed is put right on leaving the field: "0" and "" are one. */
  const settleCopies = (id: number) =>
    setTyped((prev) => ({ ...prev, [id]: String(copiesFrom(prev[id])) }));

  const onePerEquipment = () => setTyped({});

  const onePerUnit = () =>
    setTyped(Object.fromEntries(labels.map((l) => [l.id, String(copiesFrom(String(l.quantity ?? 1)))])));

  const chooseCentre = (id: number) => {
    setExcluded(new Set());
    setTyped({});
    setSearchParams(id > 0 ? { centro: String(id) } : {}, { replace: true });
  };

  return (
    <>
      <Helmet>
        <title>Etiquetas de equipamento | DIGGER</title>
        <style>{PRINT_CSS}</style>
      </Helmet>

      <div className="min-h-screen bg-muted/40">
        <div className="label-controls sticky top-0 z-10 border-b border-border bg-card">
          <div className="mx-auto max-w-6xl px-6 py-4 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <Link
                  to="/equipamentos"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Equipamentos
                </Link>
                <h1 className="mt-1 text-2xl font-bold text-foreground">
                  Etiquetas de equipamento
                </h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Escolhe quantas etiquetas queres de cada equipamento: as unidades de um
                  equipamento têm todas o mesmo código. Imprime a 100%, sem «ajustar à página», e
                  experimenta ler uma antes de cortar as outras. Em papel transparente o código só
                  se lê sobre uma superfície clara.
                </p>
              </div>

              <div className="flex items-end gap-3">
                {centers.length > 1 && (
                  <div className="flex flex-col gap-1">
                    <label htmlFor="labels-centre" className="text-xs font-medium text-muted-foreground">
                      Centro Digital
                    </label>
                    <select
                      id="labels-centre"
                      className="input"
                      value={centreId ?? 0}
                      onChange={(e) => chooseCentre(Number(e.target.value))}
                    >
                      <option value={0}>Todos os centros</option>
                      {centers.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.centerName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => window.print()}
                  disabled={chosen.length === 0}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
              </div>
            </div>

            {labels.length > 0 && (
              <fieldset>
                <legend className="text-xs font-medium text-muted-foreground">
                  {stickers.length} etiqueta{stickers.length !== 1 ? 's' : ''} · {chosen.length} de{' '}
                  {labels.length} equipamento{labels.length !== 1 ? 's' : ''} · {pages.length} folha
                  {pages.length !== 1 ? 's' : ''} A4 ({STICKERS_PER_PAGE} por folha)
                </legend>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <button type="button" onClick={onePerEquipment} className="btn-outline h-8 px-3 text-xs">
                    Uma de cada
                  </button>
                  {hasUnits && (
                    <button type="button" onClick={onePerUnit} className="btn-outline h-8 px-3 text-xs">
                      Uma por unidade
                    </button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 max-h-40 overflow-y-auto">
                  {labels.map((l) => {
                    const included = !excluded.has(l.id);
                    const units = l.quantity ?? 1;
                    return (
                      <div key={l.id} className="flex items-center gap-2 text-sm text-foreground">
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={included} onChange={() => toggle(l.id)} />
                          {l.name}
                        </label>
                        <span className="text-xs text-muted-foreground">
                          {centreId === null && centers.length > 1 && `${l.digitalCenterName} · `}
                          {units} {units === 1 ? 'unidade' : 'unidades'}
                        </span>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={MAX_COPIES}
                          step={1}
                          aria-label={`Quantas etiquetas de ${l.name}`}
                          className="input h-8 w-16 px-2 py-1 tabular-nums"
                          value={typed[l.id] ?? '1'}
                          disabled={!included}
                          onChange={(e) => setCopies(l.id, e.target.value)}
                          onBlur={() => settleCopies(l.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            )}
          </div>
        </div>

        {labelsQuery.isLoading && <LoadingBlock />}
        {labelsQuery.isError && <ErrorBlock message="Não foi possível carregar as etiquetas." />}
        {!labelsQuery.isLoading && !labelsQuery.isError && labels.length === 0 && (
          <p className="py-20 text-center text-sm text-muted-foreground">
            Não há equipamento registado{centreId !== null ? ' neste centro' : ''}.
          </p>
        )}
        {labels.length > 0 && chosen.length === 0 && (
          <p className="py-20 text-center text-sm text-muted-foreground">
            Nenhuma etiqueta escolhida.
          </p>
        )}

        <div className="label-sheets flex flex-col items-center gap-6 py-8 overflow-x-auto">
          {pages.map((page, i) => (
            // Sheets have no identity of their own: the first is the first.
            <div key={i} className="shadow-lg">
              <LabelSheet labels={page} page={i + 1} pages={pages.length} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
